import { Checkbox, Grid, Typography } from "@mui/material";
import { EvalStore } from "components/Evaluation/Stores/EvalStore";
import { observer } from "mobx-react";
import React, { FunctionComponent, useMemo } from "react";
import Question from "../../../../models/Question";
import { Tag } from "../../../../models/Tag";
import theme from "../../../../Theme/AppTheme";
import { useStore } from "../../../../utils/useStore";
import AcxStarRating from "../../../UI/AcxStarRating";
import { ModuleUIModel } from "../../Models/ModuleUIModel";
import SmartPredictToggle from "./AIExplanations/SmartPredictToggle";
import AIExplanationCards from "./AIExplanations/AIExplanationsCards";
import AcxQuestionDescription from "components/UI/AcxQuestionDescription";
import { sanitizeHtml } from "utils/sanitizeHtml";
import AcxQuestionRubric from "components/UI/AcxQuestionRubric";

function generateRatingLabels(tags: Tag[]) {
    const ratingLabels: Record<number, string> = {};
    tags!.forEach((value) => {
        ratingLabels[value.scoredValue ?? 0] = value.value;
    });
    return ratingLabels;
}

interface OwnProps {
    question: Question;
    moduleUIModel?: ModuleUIModel;
    standalone?: boolean;
    leftBorderColor?: string;
    isDisabled?: boolean;
    showDisputeCheckbox?: boolean;
}

type Props = OwnProps;

const StarRatingAnswer: FunctionComponent<Props> = observer((props) => {
    const store = useStore(EvalStore);

    const answer = store.currentEval?.getAnswerForQuestion(
        props.question,
        props.moduleUIModel?.licensedModule.evaluationModuleId,
    );

    const activeTag =
        (answer?.activeTags.length ?? 0) > 0
            ? answer?.activeTags[0]
            : undefined;

    const isSmartPredicted = answer?.hasAccuratePredictions;

    const starRatingTags = props.moduleUIModel?.getTagsFormattedForStarRating(
        props.question.id,
    );

    const maxStars = starRatingTags?.length;
    const ratingLabels = useMemo(
        () => generateRatingLabels(starRatingTags ?? []),
        [starRatingTags],
    );

    const onStarRatingChange = (rating: number | null) => {
        const arg =
            rating !== null
                ? starRatingTags?.find((value) => value.scoredValue === rating)
                : undefined;
        if (arg === undefined) {
            store.currentEval?.setAnswerForQuestion(
                props.question,
                [],
                answer,
                props.moduleUIModel?.licensedModule.evaluationModuleId,
                // store.authStore._user.profile.email,
            );
        } else {
            store.currentEval?.setAnswerForQuestion(
                props.question,
                [arg],
                answer,
                props.moduleUIModel?.licensedModule.evaluationModuleId,
                // store.authStore._user.profile.email,
            );
        }
    };

    let showAIExplanationCard = store.showAIExplanation[props.question.id];

    const hasValidDescription = !!sanitizeHtml(props.question.helperText ?? "");

    return (
        <Grid
            id={`question-${props.question.id}`}
            item
            xs={12}
            sx={{
                paddingLeft:
                    props.question.parentId || props.leftBorderColor
                        ? "16px"
                        : "0px",
                borderLeft: props.leftBorderColor
                    ? `2px solid ${props.leftBorderColor}`
                    : "none",
            }}
            container
            direction={"column"}
            justifyContent={"flex-start"}
            alignItems="stretch"
        >
            <Grid
                item
                xs={12}
                container
                direction={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                wrap={"nowrap"}
                sx={{
                    marginBottom: props.standalone
                        ? theme.spacing(0.25)
                        : theme.spacing(1),
                    marginTop: props.standalone
                        ? theme.spacing(0.5)
                        : theme.spacing(2),
                    paddingRight: theme.spacing(2),
                    paddingLeft: !props.standalone
                        ? theme.spacing(3)
                        : undefined,
                }}
            >
                {Boolean(answer && props.showDisputeCheckbox) && (
                    <Checkbox
                        id={"DisputeAnswer-" + props.question.id}
                        title="Check to Dispute this Answer."
                        checked={answer?.isDisputed ? true : false}
                        onClick={answer?.toggleIsDisputed}
                    />
                )}

                <Grid
                    container
                    item
                    direction={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"baseline"}
                    spacing={1}
                >
                    <Grid item>
                        <Typography
                            sx={{
                                color: "#1F1F1F",
                                fontSize: props.standalone ? "12px" : "14px",
                                letterSpacing: "0",
                                lineHeight: "20px",
                                fontWeight: "bold",
                            }}
                        >
                            {props.question?.questionText}
                            {props.question.required && !answer?.isAnswered && (
                                <span
                                    style={{
                                        color: theme.palette.error.main,
                                    }}
                                >
                                    *
                                </span>
                            )}
                        </Typography>
                    </Grid>
                    <SmartPredictToggle
                        question={props.question}
                        moduleUIModel={props.moduleUIModel}
                    />
                </Grid>
            </Grid>

            {!!props.question.rubric && (
                <Grid
                    item
                    container
                    xs={12}
                    sx={{
                        paddingLeft: !props.standalone
                            ? theme.spacing(3)
                            : undefined,
                        paddingRight: !props.standalone
                            ? theme.spacing(3)
                            : undefined,
                        paddingTop: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                    }}
                >
                    <AcxQuestionRubric urlString={props.question.rubric} />
                </Grid>
            )}

            {hasValidDescription && (
                <Grid
                    item
                    container
                    xs={12}
                    sx={{
                        paddingLeft: !props.standalone
                            ? theme.spacing(3)
                            : undefined,
                        paddingRight: !props.standalone
                            ? theme.spacing(3)
                            : undefined,
                    }}
                >
                    <AcxQuestionDescription
                        text={props.question.helperText ?? ""}
                        isModalForTablesVariant={props.standalone}
                    />
                </Grid>
            )}

            <Grid
                item
                xs={12}
                direction={"row"}
                wrap={"nowrap"}
                container
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
                sx={{
                    paddingLeft: !props.standalone
                        ? theme.spacing(3)
                        : undefined,
                    paddingRight: !props.standalone
                        ? theme.spacing(3)
                        : undefined,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Grid item xs={12} style={{ flexDirection: "row" }}>
                    <AcxStarRating
                        id={"StarRatingAnswer-" + props.question.id}
                        max={maxStars ?? 3}
                        iconHoverColor={theme.palette.primary.main}
                        iconFilledColor={theme.palette.primary.main}
                        ratingLabels={ratingLabels}
                        onChange={onStarRatingChange}
                        value={activeTag?.scoredValue ?? 0}
                        size={"large"}
                        disabled={props.isDisabled}
                    />
                </Grid>
            </Grid>
            {isSmartPredicted && showAIExplanationCard && activeTag && (
                <AIExplanationCards
                    activeTags={[activeTag]}
                    predictedTags={answer.predictedTags}
                    questionId={props.question.id}
                    answer={answer}
                />
            )}
        </Grid>
    );
});

export default StarRatingAnswer;
